import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import apiFiles from 'api/files';
import apiPdfEnd, { IOfferExtraPage } from 'api/offerPdfEnd';
import LinkButton from 'components/layout/buttons/LinkButton';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';
import usePastedFiles from 'services/files/usePastedFiles';
import { finishLoading, startLoading } from 'store/loading/actions';
import { addMessage } from 'store/messages/actions';

import 'style/offers/OpportunityLinks.css';

const IMAGE_TYPES = ['image/jpeg', 'image/png'];

/**
 * The images of a sales opportunity (e.g. renderings or photos of the site) as small images,
 * opened larger in a new tab. An opportunity has no PDF, so its images are kept as the
 * image pages of its PDF end (the same storage as the extra pages of an offer).
 */
const OpportunityImages = ({
  locked,
  offerId,
}: {
  locked: boolean;
  // Undefined until the opportunity is saved
  offerId?: number;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string) =>
    t(`${translations.offers}:opportunity.images.${key}`);
  const input = useRef<HTMLInputElement | null>(null);
  const [images, setImages] = useState<IOfferExtraPage[]>([]);
  // The object URLs of the loaded images by page id
  const [urls, setUrls] = useState<{ [id: number]: string }>({});
  const [dragActive, setDragActive] = useState(false);

  const load = async () => {
    if (offerId === undefined) {
      return;
    }

    try {
      const pages = await apiPdfEnd.extraPages.get('offers', offerId);
      setImages(pages.filter((page) => page.type === 'image'));
    } catch (error) {
      setImages([]);
    }
  };

  useEffect(() => {
    load();
  }, [offerId]);

  // The images are read with the rights of the user, so they are loaded as blobs
  useEffect(() => {
    if (offerId === undefined) {
      return undefined;
    }

    let active = true;
    const created: string[] = [];

    Promise.all(
      images.map(async (image) => {
        try {
          const response = await apiPdfEnd.extraPages.getFile(
            'offers',
            offerId,
            image.id
          );
          const url = URL.createObjectURL(
            new Blob([response.data], { type: image.mimeType ?? 'image/jpeg' })
          );
          created.push(url);
          return [image.id, url] as const;
        } catch (error) {
          return [image.id, ''] as const;
        }
      })
    ).then((loaded) => {
      if (active) {
        setUrls(Object.fromEntries(loaded));
      }
    });

    return () => {
      active = false;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [images, offerId]);

  // The images of the file dialog, a drop or a paste (Ctrl+V)
  const add = async (chosen: File[]) => {
    const files = chosen.filter((file) => IMAGE_TYPES.includes(file.type));

    if (offerId === undefined || files.length === 0) {
      return;
    }

    dispatch(startLoading('opportunity-images'));
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await apiFiles.post(file, null);
        // eslint-disable-next-line no-await-in-loop
        await apiPdfEnd.extraPages.save('offers', offerId, null, {
          fileId: `${uploaded.id}`,
          text: null,
          title: file.name,
          type: 'image',
        });
      }
      await load();
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    } finally {
      dispatch(finishLoading('opportunity-images'));
    }
  };

  const pasteArea = usePastedFiles(
    (files) => add(files),
    !locked && offerId !== undefined
  );

  const remove = async (image: IOfferExtraPage) => {
    if (offerId === undefined) {
      return;
    }

    try {
      await apiPdfEnd.extraPages.delete('offers', offerId, image.id);
      await load();
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    }
  };

  return (
    <div className="opportunity-images">
      <p className="header-text">{tr('Title').toUpperCase()}</p>
      {offerId === undefined ? (
        <p className="opportunity-images-info">{tr('Save first')}</p>
      ) : (
        <>
          <div className="opportunity-images-grid">
            {images.map((image) => (
              <div className="opportunity-image" key={image.id}>
                <button
                  className="opportunity-image-open"
                  onClick={() =>
                    urls[image.id]
                      ? window.open(urls[image.id], '_blank')
                      : null
                  }
                  title={image.title ?? ''}
                  type="button"
                >
                  {urls[image.id] ? (
                    <img alt={image.title ?? ''} src={urls[image.id]} />
                  ) : null}
                </button>
                {locked ? null : (
                  <span className="opportunity-image-remove">
                    <TrashIcon onClick={() => remove(image)} />
                  </span>
                )}
              </div>
            ))}
          </div>
          {locked ? null : (
            // Images are added by dropping them here, pasting them (Ctrl+V) while the
            // mouse is over the area, or from the file dialog
            <div
              className={`opportunity-images-drop${
                dragActive ? ' active' : ''
              }`}
              onDragLeave={(event) => {
                if (
                  !event.currentTarget.contains(event.relatedTarget as Node)
                ) {
                  setDragActive(false);
                }
              }}
              onDragOver={(event) => {
                if (Array.from(event.dataTransfer.types).includes('Files')) {
                  event.preventDefault();
                  setDragActive(true);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                add(Array.from(event.dataTransfer.files));
              }}
              ref={pasteArea}
            >
              <input
                accept={IMAGE_TYPES.join(',')}
                hidden
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  // eslint-disable-next-line no-param-reassign
                  event.target.value = '';
                  add(files);
                }}
                ref={input}
                type="file"
              />
              <LinkButton onClick={() => input.current?.click()}>
                {`+ ${tr('Add')}`}
              </LinkButton>
              <span className="opportunity-images-drop-hint">
                {tr('Drop hint')}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OpportunityImages;
